import React, {useEffect, useState} from "react";
import ReactMarkdown from "react-markdown";
import "./App.css";

const DATA_CONFIG_URL = process.env.REACT_APP_DATASETS_URL || "/datasets.json";
const DOWNLOAD_FUNCTION_URL = process.env.REACT_APP_DOWNLOAD_URL || "";
const HEADER_LOGO_URL = "https://cdn.prod.website-files.com/67a15cdf1b8bba2d544558bc/67a16bd6611cbd938fc47dc1_Full%20Logo%20in%20White.png";

const readmeContent = `
### Notes
#### Output 
Each download is a tar file. See [Singular Documentation](https://docs.singulargenomics.com) for details on files and structure within the download

#### Compatibility with the [G4X-viewer](https://g4x-viewer.singulargenomics.com/)

- The **.bin** file contains the segmentation mask.
- The **.tar** file contains the transcripts.
- The **<sample_id>.ome.tiff** file contains all protein, nuclear, and eosin images.
- The **<sample_id>_nuclear.ome.tiff** file contains only the nuclear and eosin images.
- The **<sample_id>_HE.ome.tiff** file contains the H&E image.

`;

const deriveGroups = (config) => {
    if (Array.isArray(config.groups) && config.groups.length > 0) {
        return config.groups;
    }
    const uniqueKeys = Array.from(
        new Set((config.datasets || []).map((dataset) => dataset.group).filter(Boolean))
    );
    return uniqueKeys.map((key) => ({key, title: key}));
};

const App = () => {

    const [datasets, setDatasets] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [configError, setConfigError] = useState(null);
    const [visibleChecksumId, setVisibleChecksumId] = useState(null);
    const [copiedChecksumId, setCopiedChecksumId] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());
    const [previewDataset, setPreviewDataset] = useState(null);

    const setExpandedToAllGroups = (groupList) => {
        const keys = (groupList || []).map((group) => group.key);
        setExpandedGroups(new Set(keys));
    };

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(DATA_CONFIG_URL, {cache: "no-store"});
                if (!response.ok) {
                    throw new Error(`Failed to fetch dataset config (${response.status})`);
                }
                const config = await response.json();
                if (!config || !Array.isArray(config.datasets)) {
                    throw new Error("Config missing datasets array");
                }

                setDatasets(config.datasets);
                const derivedGroups = deriveGroups(config);
                setGroups(derivedGroups);
                setExpandedToAllGroups(derivedGroups);
                setConfigError(null);
            } catch (error) {
                console.error("Error loading dataset config:", error);
                setDatasets([]);
                setGroups([]);
                setExpandedGroups(new Set());
                setVisibleChecksumId(null);
                setConfigError(`Failed to load remote configuration: ${DATA_CONFIG_URL}`);
            } finally {
                setLoadingConfig(false);
            }
        };

        fetchConfig();
    }, []);

        const handleDownload = async (datasetId) => {
        if (!DOWNLOAD_FUNCTION_URL) {
            console.error("Missing REACT_APP_DOWNLOAD_URL env variable");
            setConfigError("Download service is not configured.");
            return;
        }

        const url = new URL(DOWNLOAD_FUNCTION_URL);
        url.searchParams.set("datasetId", datasetId);

        try {
            const response = await fetch(url.toString());

            if (!response.ok) {
                throw new Error("Failed to fetch download URL");
            }

            const data = await response.json();
            const downloadUrl = data.url;

            window.location.href = downloadUrl;
        } catch (error) {
            console.error("Error downloading dataset:", error);
        }
    };

    const toggleChecksumVisibility = (datasetId) => {
        setVisibleChecksumId((prev) => (prev === datasetId ? null : datasetId));
        setCopiedChecksumId(null);
    };

    const handleCopyChecksum = async (datasetId, checksum) => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(checksum);
            }
            setCopiedChecksumId(datasetId);
        } catch (error) {
            console.error("Failed to copy checksum:", error);
        }
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
        setVisibleChecksumId(null);
        setCopiedChecksumId(null);
    };

    const closePreview = () => {
        setPreviewDataset(null);
        setVisibleChecksumId(null);
        setCopiedChecksumId(null);
    };

    return (
        <div className="p-6 max-w-6xl mx-auto  app-container">
            <header className="app-header">
                <div className="app-header__content">
                    <div className="app-header__logo-wrap">
                        <img src={HEADER_LOGO_URL} alt="Singular Genomics" className="app-header__logo"/>
                    </div>
                    <h1 className="app-header__title">G4X Multiomic Data Portal</h1>
                </div>
            </header>
            <main>
                {loadingConfig && (
                    <div className="config-status" role="status">
                        Loading dataset catalog...
                    </div>
                )}
                {configError && !loadingConfig && (
                    <div className="config-status config-status--error">
                        {configError}
                    </div>
                )}
                <div className="p-6 max-w-6xl mx-auto dataset-wrapper">
                    {groups.map((group) => {
                        const items = datasets.filter((d) => d.group === group.key);
                        if (items.length === 0) return null;
                        return (

                            <section key={group.key} className="group-accordion">
                                <button className="accordion-toggle" onClick={() => toggleGroup(group.key)}>
                                    <span>{group.title}</span>
                                    <span className="accordion-icon">{expandedGroups.has(group.key) ? "−" : "+"}</span>
                                </button>
                                {expandedGroups.has(group.key) && (
                                    <div className="dataset-grid">
                                        {items.map((dataset) => (
                                            <div key={dataset.id}
                                                 className="dataset-card compact border rounded-2xl p-4">
                                                <div className="dataset-thumb">
                                                    <img src={dataset.imageUrl} alt={dataset.name}
                                                         className="dataset-image-thumb"/>
                                                </div>
                                                <div className="dataset-info">
                                                    <h3>{dataset.name}</h3>
                                                    {dataset.dataSize && (
                                                        <p className="dataset-size">{dataset.dataSize}</p>
                                                    )}
                                                </div>
                                                <div className="button-row">
                                                    <button
                                                        className="download-button px-3 py-2 rounded-xl border hover:shadow text-sm"
                                                        onClick={() => handleDownload(dataset.id)}
                                                    >
                                                        Download
                                                    </button>
                                                    <button
                                                        className="preview-button px-3 py-2 rounded-xl border hover:shadow text-sm"
                                                        onClick={() => setPreviewDataset(dataset)}
                                                    >
                                                        Details
                                                    </button>
                                                </div>

                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                <section className="readme max-w-6xl mx-auto">
                    <ReactMarkdown className="readme-content">{readmeContent}</ReactMarkdown>
                </section>
                <section className="section u-height-half_screen u-padding-footer footer cc-contact">
                    <div className="w-layout-blockcontainer u-height-full footer w-container">
                        <div className="footer_container">
                            <div className="grid-cols cc-4col"><img
                                src="https://cdn.prod.website-files.com/67a15cdf1b8bba2d544558bc/67a16bd6611cbd938fc47dc1_Full%20Logo%20in%20White.png"
                                loading="lazy" id="w-node-_62582f4b-8d3f-f050-c1c6-32503cdc96c2-3cdc96be" alt=""
                                className="image-3"/>
                                <div className="footer-nav">
                                    <div className="sg-folder footer-nav">
                                        <div className="text-lg">G4X Platform</div>
                                <div className="sg-subfolder"><a href="https://singulargenomics.com/g4x"
                                                                         className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Overview</div>
                                </a><a href="https://singulargenomics.com/g4x-platform-ecosystem"
                                               className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Platform and Ecosystem</div>
                                </a><a href="https://singulargenomics.com/application-performance"
                                               className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Performance</div>
                                </a><a href="https://singulargenomics.com/content"
                                               className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Content</div>
                                </a><a href="https://singulargenomics.com/pricing"
                                               className="sg-side_nav-sub-link-copy w-inline-block">
                                    <div className="text-sm">Pricing</div>
                                </a><a href="https://singulargenomics.com/sequencing"
                                               className="sg-side_nav-sub-link-copy w-inline-block">
                                    <div className="text-sm">Sequencing on G4X</div>
                                </a><a href="https://singulargenomics.com/spatial-services"
                                               className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Spatial Services</div>
                                </a></div>
                            </div>
                        </div>
                        <div className="footer-nav">
                            <div className="sg-folder footer-nav">
                                <div className="text-lg">Resources</div>
                                <div className="sg-subfolder"><a href="https://singulargenomics.com/publications-and-videos"
                                                                         className="sg-side_nav-sub-link w-inline-block">
                                    <div className="text-sm">Videos and Publications</div>
                                </a><a href="https://support.singulargenomics.com"
                                               className="sg-side_nav-sub-link w-inline-block">
                                            <div className="text-sm">Sequencing Support Hub</div>
                                        </a></div>
                                    </div>
                                </div>
                            <div className="footer-nav">
                                <div className="sg-folder footer-nav">
                                    <div className="text-lg">Company</div>
                                    <div className="sg-subfolder"><a href="https://singulargenomics.com/about-us"
                                                                         className="sg-side_nav-sub-link w-inline-block">
                                        <div className="text-sm">About Us</div>
                                    </a><a href="https://singulargenomics.com/news"
                                               className="sg-side_nav-sub-link w-inline-block">
                                        <div className="text-sm">News and Events</div>
                                    </a><a href="https://singulargenomics.com/contact-us"
                                               className="sg-side_nav-sub-link w-inline-block">
                                        <div className="text-sm">Contact</div>
                                    </a></div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid-cols">
                                <div id="w-node-_62582f4b-8d3f-f050-c1c6-32503cdc96fa-3cdc96be"
                                     className="footer_connect">
                                </div>
                            </div>
                            <div className="skinny">
                                <div className="text-xs cc-grey">@ 2025 Singular Genomics Systems, Inc. All rights
                                    reserved.
                                    Product in Development. For Research Use Only. | <a href="https://singulargenomics.com/legal">Legal</a> | <a
                                        href="https://singulargenomics.com/legal/privacy">
                                        Privacy Policy</a></div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            {previewDataset && (
                <div className="preview-modal" role="dialog" aria-modal="true">
                    <div className="preview-modal__content">
                        <button className="preview-modal__close" onClick={closePreview}>✕</button>
                        <h3>{previewDataset.name}</h3>
                        <img src={previewDataset.imageUrl} alt={previewDataset.name}/>
                        {previewDataset.details && (
                            <div
                                className="preview-details"
                                // Render the inline HTML in the details text
                                dangerouslySetInnerHTML={{__html: previewDataset.details}}
                            />
                        )}
                        <div className="button-row">
                            <button
                                className="download-button px-3 py-2 rounded-xl border hover:shadow text-sm"
                                onClick={() => {
                                    handleDownload(previewDataset.id);
                                    closePreview();
                                }}
                            >
                                Download
                            </button>
                            <button
                                className="checksum-button px-3 py-2 rounded-xl border hover:shadow text-sm"
                                onClick={() => toggleChecksumVisibility(previewDataset.id)}
                            >
                                {visibleChecksumId === previewDataset.id ? "Hide checksum" : "Checksum"}
                            </button>
                        </div>
                        {visibleChecksumId === previewDataset.id && (
                            <div className="checksum-panel">
                                <code>{previewDataset.dataChecksum}</code>
                                <div className="checksum-actions">
                                    <button
                                        className="checksum-button px-2 py-1 rounded border text-xs"
                                        onClick={() => handleCopyChecksum(previewDataset.id, previewDataset.dataChecksum)}
                                    >
                                        {copiedChecksumId === previewDataset.id ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
        ;
};

export default App;
